/**
 * Monday.com API Integration
 * GraphQL API client for fetching board data
 */

const MONDAY_API_URL = 'https://api.monday.com/v2';
const API_TOKEN = process.env.REACT_APP_MONDAY_API_TOKEN?.replace(/"/g, ''); // Remove quotes if present
const BOARD_ID = process.env.REACT_APP_MONDAY_BOARD_ID;

/**
 * Execute a GraphQL query against Monday.com API
 */
async function mondayQuery(query, variables = {}) {
  if (!API_TOKEN) {
    throw new Error('REACT_APP_MONDAY_API_TOKEN not configured in .env');
  }

  const response = await fetch(MONDAY_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': API_TOKEN,
      'API-Version': '2024-01'
    },
    body: JSON.stringify({ query, variables })
  });

  if (!response.ok) {
    throw new Error(`Monday API error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();

  if (result.errors) {
    throw new Error(`Monday API GraphQL errors: ${JSON.stringify(result.errors)}`);
  }

  return result.data;
}

/**
 * Fetch board structure and all items
 */
export async function fetchBoard(boardId = BOARD_ID) {
  const query = `
    query ($boardId: [ID!]) {
      boards(ids: $boardId) {
        id
        name
        description
        board_kind
        state
        columns {
          id
          title
          type
          settings_str
        }
        groups {
          id
          title
          color
          position
        }
        items_page(limit: 500) {
          cursor
          items {
            id
            name
            state
            created_at
            updated_at
            group {
              id
              title
            }
            column_values {
              id
              text
              value
              type
            }
            subitems {
              id
              name
              column_values {
                id
                text
                value
              }
            }
          }
        }
      }
    }
  `;

  const data = await mondayQuery(query, { boardId: [parseInt(boardId)] });
  return data.boards[0];
}

/**
 * Fetch board metadata only (columns, groups)
 */
export async function fetchBoardStructure(boardId = BOARD_ID) {
  const query = `
    query ($boardId: [ID!]) {
      boards(ids: $boardId) {
        id
        name
        description
        columns {
          id
          title
          type
          settings_str
        }
        groups {
          id
          title
          color
        }
      }
    }
  `;

  const data = await mondayQuery(query, { boardId: [parseInt(boardId)] });
  return data.boards[0];
}

/**
 * Update an item's column value
 */
export async function updateItemColumn(itemId, columnId, value) {
  const query = `
    mutation ($itemId: ID!, $columnId: String!, $value: JSON!) {
      change_column_value(item_id: $itemId, column_id: $columnId, value: $value) {
        id
      }
    }
  `;

  return mondayQuery(query, {
    itemId: parseInt(itemId),
    columnId,
    value: JSON.stringify(value)
  });
}

/**
 * Create a new item in a group
 */
export async function createItem(boardId, groupId, itemName, columnValues = {}) {
  const query = `
    mutation ($boardId: ID!, $groupId: String!, $itemName: String!, $columnValues: JSON) {
      create_item(
        board_id: $boardId
        group_id: $groupId
        item_name: $itemName
        column_values: $columnValues
      ) {
        id
        name
      }
    }
  `;

  return mondayQuery(query, {
    boardId: parseInt(boardId),
    groupId,
    itemName,
    columnValues: JSON.stringify(columnValues)
  });
}

/**
 * Parse Monday.com column value based on type
 */
export function parseColumnValue(columnValue) {
  const { type, text, value } = columnValue;

  if (!value || value === '{}' || value === 'null') {
    return null;
  }

  try {
    const parsed = JSON.parse(value);

    switch (type) {
      case 'status':
        return parsed.label || text;
      case 'date':
        return parsed.date || text;
      case 'timeline':
        return { from: parsed.from, to: parsed.to };
      case 'people':
        return parsed.personsAndTeams?.map(p => ({
          id: p.id,
          name: p.name || text
        })) || [];
      case 'numbers':
        return parseFloat(text) || 0;
      case 'text':
      case 'long-text':
        return text;
      case 'dropdown':
        return parsed.labels || [];
      case 'email':
        return parsed.email || text;
      case 'phone':
        return parsed.phone || text;
      default:
        return text;
    }
  } catch (e) {
    return text;
  }
}

const mondayApi = {
  fetchBoard,
  fetchBoardStructure,
  updateItemColumn,
  createItem,
  parseColumnValue,
  query: mondayQuery
};

export default mondayApi;
